import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useId, useMemo, } from "react";
import { responsiveBodySmClassName } from "../shared/typography-responsive";
import { cn } from "../utils";
import { radioContainerVariants, radioGroupVariants, radioInnerVariants, radioLabelVariants, radioOuterVariants, } from "./radio.styles";
/**
 * Radio component following Material Design 3 specifications.
 *
 * @example
 * ```tsx
 * <Radio label="Option A" name="options" value="a" />
 * ```
 */
export function Radio({ label, helperText, error = false, size = "medium", disabled, selected, value, name, id: providedId, onChange, className, testID, ref, }) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const handleChange = () => {
        onChange?.(value);
    };
    return (_jsxs("label", { htmlFor: id, className: cn(radioContainerVariants({ disabled: Boolean(disabled) }), "group", className), children: [_jsxs("span", { className: radioOuterVariants({
                    size,
                    checked: Boolean(selected),
                    error,
                    disabled: Boolean(disabled),
                }), children: [_jsx("span", { className: radioInnerVariants({
                            size,
                            checked: Boolean(selected),
                            error,
                        }) }), _jsx("input", { ref: ref, type: "radio", id: id, name: name, value: value, disabled: disabled, checked: selected, onChange: handleChange, className: "sr-only", "data-testid": testID })] }), (label || helperText) && (_jsxs("span", { className: "flex flex-col", children: [label && (_jsx("span", { className: radioLabelVariants({ size }), children: label })), helperText && (_jsx("span", { className: "text-sm text-on-surface-variant", children: helperText }))] }))] }));
}
const RadioGroupContext = createContext(null);
/**
 * RadioGroup component for managing a group of radio options.
 *
 * @example
 * ```tsx
 * <RadioGroup name="size" value={size} onChange={setSize} label="Size">
 *   <RadioGroup.Item value="small" label="Small" />
 *   <RadioGroup.Item value="medium" label="Medium" />
 *   <RadioGroup.Item value="large" label="Large" />
 * </RadioGroup>
 * ```
 */
export function RadioGroup({ name, value, onChange, label, disabled, error, children, orientation = "vertical", disableDefaultGap = false, testID, }) {
    const contextValue = useMemo(() => ({ name, value, onChange, disabled, error: Boolean(error) }), [name, value, onChange, disabled, error]);
    return (_jsx(RadioGroupContext.Provider, { value: contextValue, children: _jsxs("fieldset", { className: "flex flex-col gap-2", disabled: disabled, "data-testid": testID, children: [label && (_jsx("legend", { className: "text-label-md text-on-surface-variant mb-1", children: label })), _jsx("div", { className: radioGroupVariants({
                        orientation,
                        gap: disableDefaultGap ? "none" : "default",
                    }), children: children }), error && (_jsx("span", { className: cn(responsiveBodySmClassName, "text-error"), children: error }))] }) }));
}
/**
 * RadioGroup.Item - Individual radio option within a RadioGroup
 */
function RadioGroupItem({ value, label, helperText, disabled, size, className, testID, id, ref, }) {
    const context = useContext(RadioGroupContext);
    if (!context) {
        throw new Error("RadioGroup.Item must be used within a RadioGroup");
    }
    const { name, value: groupValue, onChange, disabled: groupDisabled, error, } = context;
    const isSelected = groupValue === value;
    const isDisabled = disabled || groupDisabled;
    return (_jsx(Radio, { name: name, value: value, selected: isSelected, onChange: onChange, disabled: isDisabled, label: label, helperText: helperText, error: error, size: size, className: className, testID: testID, id: id, ref: ref }));
}
RadioGroup.Item = RadioGroupItem;
//# sourceMappingURL=Radio.js.map